import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { getFAQs } from '@/config/faqs.config';
import { FAQ } from '@/types/faq.types';
import DriveSourceIcon from '@/assets/DriveSource Favicon White.svg';
import { SchemaMarkup } from '@/components/common/SchemaMarkup';
import { CanonicalTag } from '@/components/common/CanonicalTag';

const TOPIC_ORDER = [
    'Maintenance & Repairs',
    'Warning Lights & Troubleshooting',
    'Buying a Car',
    'Reliability & Longevity',
    'Resale Value',
];

const TOPIC_DESCRIPTIONS: Record<string, string> = {
    'Maintenance & Repairs': 'Keep your vehicle running smoothly and understand what repairs really cost.',
    'Warning Lights & Troubleshooting': 'Decode dashboard warnings and diagnose common car problems.',
    'Buying a Car': 'Make a smarter purchase — new, used, or electric.',
    'Reliability & Longevity': 'Find out how long your car should last and what affects its lifespan.',
    'Resale Value': 'Learn what holds a vehicle\'s value and how to get the most when you sell.',
};

const TOPIC_PROMPTS: Record<string, string[]> = {
    'Maintenance & Repairs': [
        'Find me a car with low maintenance costs near me under $45,000',
        'Show me reliable vehicles with cheap upkeep near me under $55,000',
        'Find me a new SUV known for low repair costs near me under $50,000',
    ],
    'Warning Lights & Troubleshooting': [
        'Find me a reliable car with few electrical problems near me under $45,000',
        'Show me vehicles with strong reliability ratings near me under $55,000',
        'Find me a car that rarely has mechanical issues under $40,000',
    ],
    'Buying a Car': [
        'Find me a new car near me under $45,000',
        'Show me the best value new vehicles near me under $55,000',
        'Find me a new SUV near me under $60,000',
    ],
    'Reliability & Longevity': [
        'Find me a reliable car near me under $45,000',
        'Show me vehicles known for longevity near me under $55,000',
        'Find me a long-lasting new truck under $60,000',
    ],
    'Resale Value': [
        'Find me a car with good resale value near me under $50,000',
        'Show me trucks and SUVs that hold their value near me under $60,000',
        'Find me a vehicle with strong resale value near me under $55,000',
    ],
};

export const FAQIndexPage: React.FC = () => {
    const navigate = useNavigate();
    const faqs = getFAQs();

    const faqIndexSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: `Find the full answer at https://getdrivesource.com${faq.path}`,
            },
        })),
    };

    // Group FAQs by topic
    const grouped = faqs.reduce<Record<string, FAQ[]>>((acc, faq) => {
        const topic = faq.topic || 'General';
        if (!acc[topic]) acc[topic] = [];
        acc[topic].push(faq);
        return acc;
    }, {});

    // Sort topics by predefined order, then alphabetically for any extras
    const sortedTopics = [
        ...TOPIC_ORDER.filter(t => grouped[t]),
        ...Object.keys(grouped).filter(t => !TOPIC_ORDER.includes(t)).sort(),
    ];

    return (
        <>
            <CanonicalTag href="https://getdrivesource.com/faq" />
            <SchemaMarkup schema={faqIndexSchema} />
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-48 sm:pb-36 px-4 sm:px-6">
                <div className="max-w-3xl mx-auto">
                    {/* Hero */}
                    <div className="text-center mb-10 sm:mb-14">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">
                            Frequently Asked Questions
                        </h1>
                        <p className="text-gray-300 text-sm sm:text-base max-w-xl mx-auto">
                            Owning a car in Canada comes with unique challenges, from winter driving to rising repair costs. Our guides help drivers understand maintenance, diagnostics, and buying decisions.
                        </p>
                    </div>

                    {/* Topic sections */}
                    <div className="space-y-10">
                        {sortedTopics.map(topic => {
                            const prompts = TOPIC_PROMPTS[topic] ?? [];
                            return (
                                <section key={topic}>
                                    <div className="mb-4 pb-2 border-b border-gray-700">
                                        <h2 className="text-lg sm:text-xl font-bold text-white">{topic}</h2>
                                        {TOPIC_DESCRIPTIONS[topic] && (
                                            <p className="text-gray-400 text-xs sm:text-sm mt-1">
                                                {TOPIC_DESCRIPTIONS[topic]}
                                            </p>
                                        )}
                                    </div>
                                    <ul className="space-y-2">
                                        {grouped[topic].map(faq => (
                                            <li key={faq.path}>
                                                <button
                                                    onClick={() => navigate(faq.path)}
                                                    className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl bg-[#1a1a1a] hover:bg-cyan-400/10 border border-gray-700 hover:border-cyan-400/40 transition-all text-left group min-h-[44px]"
                                                >
                                                    <span className="text-sm sm:text-base text-gray-200 group-hover:text-white transition-colors">
                                                        {faq.question}
                                                    </span>
                                                    <ChevronRight className="size-4 text-gray-500 group-hover:text-cyan-400 flex-shrink-0 transition-colors" />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>

                                    {prompts.length > 0 && (
                                        <div className="mt-3 grid grid-cols-2 gap-2">
                                            {prompts.map((prompt, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => navigate(`/?q=${encodeURIComponent(prompt)}`)}
                                                    className="flex items-center gap-1.5 px-3 sm:px-4 py-2.5 sm:py-2 rounded-3xl border border-gray-500 bg-[#2d2d2d] text-gray-300 text-xs sm:text-sm hover:border-gray-300 hover:text-white transition-colors w-full min-h-[44px] sm:min-h-0 text-left"
                                                >
                                                    <img src={DriveSourceIcon} alt="" className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0 object-contain" />
                                                    <span className="leading-tight">{prompt}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </section>
                            );
                        })}
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};
